import { useParams, Link, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { motion } from 'framer-motion'
import servicesData from '../data/servicesData'
import { getServiceIcon } from '../components/ServiceIcons'
import '../styles/pages/ServiceDetailPage.css'

// Hero images for each service
const serviceHeroImages = {
  brand: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1600&auto=format&fit=crop&q=80',
  software: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1600&auto=format&fit=crop&q=80',
  ai: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1600&auto=format&fit=crop&q=80',
  transform: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1600&auto=format&fit=crop&q=80',
  ux: 'https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=1600&auto=format&fit=crop&q=80',
  data: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1600&auto=format&fit=crop&q=80',
  marketing: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=1600&auto=format&fit=crop&q=80',
  security: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=1600&auto=format&fit=crop&q=80',
  hardware: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&auto=format&fit=crop&q=80',
  consulting: 'https://images.unsplash.com/photo-1531545514256-b1400bc00f31?w=1600&auto=format&fit=crop&q=80',
}

// Extended service content for detail pages
const serviceContent = {
  brand: {
    headline: "Build a Brand That Commands Trust",
    subheadline: "Stop losing deals to unprofessional perception. Your brand is your first impression — make it count.",
    overview: "Your brand is more than a logo. It's the promise you make to every customer, the trust you build before a single word is spoken. At Figo Digital, we craft brand identities that resonate with your target audience and position you as a leader in your industry.",
    benefits: [
      { title: "Increase Perceived Value", description: "A professional brand identity can increase your perceived value by up to 23%, allowing you to command premium pricing." },
      { title: "Build Instant Trust", description: "First impressions matter. A cohesive brand identity builds credibility from the first touchpoint." },
      { title: "Stand Out from Competition", description: "In crowded markets, a distinctive brand helps you capture attention and stay memorable." },
      { title: "Attract Better Clients", description: "Quality branding attracts quality clients who value what you offer and are willing to pay for it." }
    ],
    process: [
      { step: "Discovery", description: "We dive deep into your business, audience, competitors, and goals to understand what makes you unique." },
      { step: "Strategy", description: "We develop a brand strategy that positions you effectively in your market and resonates with your ideal customers." },
      { step: "Design", description: "Our designers create visual elements that bring your brand strategy to life — logo, colors, typography, and more." },
      { step: "Delivery", description: "You receive a complete brand package with guidelines, assets, and everything you need to maintain consistency." }
    ],
    cta: "Ready to transform your brand?"
  },
  software: {
    headline: "Custom Software That Multiplies Revenue",
    subheadline: "Your competitors run on software. Your business runs on paper. It's time to change that.",
    overview: "Off-the-shelf software forces you to adapt your business to its limitations. Custom software adapts to your business, automating your unique processes and giving you competitive advantages others can't buy.",
    benefits: [
      { title: "Automate Operations", description: "Replace manual processes with automated workflows that run 24/7 without errors or fatigue." },
      { title: "Scale Without Breaking", description: "Custom software grows with you. Handle 10x the volume without 10x the staff." },
      { title: "Own Your Technology", description: "No monthly fees to vendors. No feature limitations. No dependency on third parties." },
      { title: "Gain Competitive Edge", description: "Build capabilities your competitors can't copy because they're uniquely yours." }
    ],
    process: [
      { step: "Requirements Analysis", description: "We map your current processes, identify pain points, and define exactly what your software needs to do." },
      { step: "Architecture & Design", description: "We design a scalable, secure system architecture and create user interface mockups for your approval." },
      { step: "Agile Development", description: "We build in sprints, delivering working features regularly so you can see progress and provide feedback." },
      { step: "Testing & Launch", description: "Rigorous testing ensures quality. We handle deployment, training, and support for a smooth launch." }
    ],
    cta: "Let's build your competitive advantage"
  },
  ai: {
    headline: "AI That Works While You Sleep",
    subheadline: "Stop paying staff to copy data, chase approvals, or file receipts. Let AI handle the grunt work.",
    overview: "Artificial Intelligence isn't just for tech giants anymore. We help businesses of all sizes implement practical AI solutions that automate repetitive tasks, provide instant customer service, and generate insights from your data.",
    benefits: [
      { title: "24/7 Customer Service", description: "AI chatbots handle customer inquiries instantly, any time of day, in any language." },
      { title: "Eliminate Manual Data Entry", description: "AI extracts data from documents, emails, and forms automatically with high accuracy." },
      { title: "Predictive Insights", description: "Forecast demand, identify risks, and spot opportunities before your competitors do." },
      { title: "Content at Scale", description: "Generate marketing copy, reports, and documentation in seconds, not hours." }
    ],
    process: [
      { step: "AI Audit", description: "We analyze your operations to identify the highest-impact opportunities for AI automation." },
      { step: "Solution Design", description: "We select the right AI technologies and design workflows that integrate with your existing systems." },
      { step: "Training & Customization", description: "We train AI models on your data and customize behavior to match your brand and requirements." },
      { step: "Deployment & Optimization", description: "We deploy, monitor, and continuously improve your AI systems for better results over time." }
    ],
    cta: "Automate your business with AI"
  },
  transform: {
    headline: "Transform Chaos into Scalable Systems",
    subheadline: "Your competitors are digitizing. Your customers expect more. Are you ready?",
    overview: "Digital transformation isn't about technology — it's about survival. We help businesses modernize their operations, connect their systems, and build the digital infrastructure needed to compete in today's economy.",
    benefits: [
      { title: "Unified Operations", description: "Connect siloed systems into a single source of truth. No more spreadsheet chaos." },
      { title: "Real-time Visibility", description: "See what's happening across your entire business instantly, not days later." },
      { title: "Audit-Ready Systems", description: "Digital records, automated compliance, and clear audit trails reduce risk and stress." },
      { title: "Future-Proof Foundation", description: "Build on modern platforms that can evolve as technology and your business change." }
    ],
    process: [
      { step: "Current State Assessment", description: "We document your existing processes, systems, and pain points to understand where you are." },
      { step: "Future State Design", description: "We design your ideal digital operations with clear roadmap and prioritization." },
      { step: "Phased Implementation", description: "We transform your business in manageable phases, minimizing disruption while maximizing value." },
      { step: "Change Management", description: "We train your team and ensure adoption so your investment delivers real results." }
    ],
    cta: "Start your digital transformation"
  },
  ux: {
    headline: "Design That Converts Visitors to Customers",
    subheadline: "Great tech fails when real humans can't use it. We design experiences that drive results.",
    overview: "User experience is the difference between software people love and software they abandon. We combine research, psychology, and design expertise to create digital experiences that are intuitive, enjoyable, and effective.",
    benefits: [
      { title: "Higher Conversion Rates", description: "Good UX can increase conversion rates by 200-400%. Every improvement pays for itself." },
      { title: "Reduced Support Costs", description: "Intuitive interfaces mean fewer confused users and fewer support tickets." },
      { title: "Increased User Retention", description: "Users stay with products that are pleasant to use. Reduce churn through better experience." },
      { title: "Faster User Onboarding", description: "New users become productive faster when your interface guides them naturally." }
    ],
    process: [
      { step: "User Research", description: "We study your users through interviews, surveys, and analytics to understand their needs and behaviors." },
      { step: "Information Architecture", description: "We organize content and features in ways that match how users think and work." },
      { step: "Wireframing & Prototyping", description: "We create interactive prototypes to test ideas before investing in full development." },
      { step: "Visual Design & Testing", description: "We design beautiful interfaces and validate them with real users before launch." }
    ],
    cta: "Create experiences users love"
  },
  data: {
    headline: "Turn Data into Decisions",
    subheadline: "Your competitors aren't smarter — they just see the numbers faster.",
    overview: "Data is useless until it becomes insight. Insight is useless until it drives action. We help businesses collect, analyze, and visualize their data so decision-makers can act with confidence.",
    benefits: [
      { title: "Faster Decisions", description: "Real-time dashboards mean you can respond to changes in hours, not weeks." },
      { title: "Identify Hidden Patterns", description: "Analytics reveal trends and correlations that human analysis would miss." },
      { title: "Measure What Matters", description: "Track the KPIs that actually drive your business, not vanity metrics." },
      { title: "Predict the Future", description: "Predictive analytics help you anticipate demand, risks, and opportunities." }
    ],
    process: [
      { step: "Data Audit", description: "We inventory your data sources, assess quality, and identify gaps in your data infrastructure." },
      { step: "Architecture Design", description: "We design data pipelines that collect, clean, and centralize your data securely." },
      { step: "Dashboard Development", description: "We build interactive dashboards that make complex data accessible to everyone." },
      { step: "Training & Adoption", description: "We ensure your team knows how to use and trust the insights to make better decisions." }
    ],
    cta: "Unlock the power of your data"
  },
  marketing: {
    headline: "Be Visible Where Your Customers Are",
    subheadline: "Your competitors aren't better — they're just visible. Turn your website into a 24/7 salesperson.",
    overview: "Digital marketing isn't about being everywhere — it's about being in the right places with the right message. We help businesses attract, engage, and convert their ideal customers through strategic digital marketing.",
    benefits: [
      { title: "Consistent Lead Flow", description: "Build marketing systems that generate qualified leads predictably, month after month." },
      { title: "Lower Customer Acquisition Cost", description: "Optimize campaigns continuously to get more customers for less money." },
      { title: "Measurable ROI", description: "Track every naira spent and know exactly what's working and what's not." },
      { title: "Brand Authority", description: "Become the go-to expert in your space through strategic content and visibility." }
    ],
    process: [
      { step: "Marketing Audit", description: "We analyze your current marketing, competitors, and opportunities to identify quick wins." },
      { step: "Strategy Development", description: "We create a comprehensive marketing plan aligned with your business goals and budget." },
      { step: "Campaign Execution", description: "We implement campaigns across the channels that matter most for your audience." },
      { step: "Optimization & Reporting", description: "We monitor, test, and improve continuously while keeping you informed of progress." }
    ],
    cta: "Grow your business online"
  },
  security: {
    headline: "Protect Your Business from Digital Threats",
    subheadline: "One breach can destroy years of trust. Don't wait until it's too late.",
    overview: "Cybersecurity isn't optional anymore. With increasing regulations and sophisticated threats, businesses need proactive protection. We help you identify vulnerabilities, implement safeguards, and ensure compliance.",
    benefits: [
      { title: "Prevent Costly Breaches", description: "The average data breach costs millions. Prevention is far cheaper than recovery." },
      { title: "Maintain Customer Trust", description: "Show customers their data is safe with you through certified security practices." },
      { title: "Ensure Compliance", description: "Meet NDPR, GDPR, and industry regulations to avoid fines and legal issues." },
      { title: "Business Continuity", description: "Prepare for incidents so your business can recover quickly if something happens." }
    ],
    process: [
      { step: "Security Assessment", description: "We evaluate your current security posture and identify vulnerabilities and risks." },
      { step: "Strategy & Roadmap", description: "We prioritize improvements based on risk and create a practical implementation plan." },
      { step: "Implementation", description: "We deploy security tools, update policies, and harden your systems against threats." },
      { step: "Training & Monitoring", description: "We train your team on security best practices and set up ongoing monitoring." }
    ],
    cta: "Secure your business today"
  },
  hardware: {
    headline: "Business-Grade Hardware, Delivered Ready",
    subheadline: "Stop wasting time shopping. We source, setup, and support everything you need.",
    overview: "Running a digital business requires the right hardware. We provide business-grade equipment — from laptops to POS systems to security cameras — delivered configured and ready to use, with ongoing support.",
    benefits: [
      { title: "Save Time", description: "No more researching, comparing, and shopping. We handle procurement end-to-end." },
      { title: "Business-Grade Quality", description: "We select reliable equipment that performs in demanding business environments." },
      { title: "Pre-Configured & Ready", description: "Equipment arrives set up for your systems. Plug in and start working immediately." },
      { title: "Ongoing Support", description: "When something breaks, we're here to help with repairs, replacements, and upgrades." }
    ],
    process: [
      { step: "Requirements Assessment", description: "We understand your business needs to recommend the right hardware solutions." },
      { step: "Procurement", description: "We source quality equipment at competitive prices from trusted suppliers." },
      { step: "Configuration", description: "We set up and configure everything to work with your existing systems." },
      { step: "Delivery & Support", description: "We deliver, install if needed, and provide ongoing support and maintenance." }
    ],
    cta: "Get the hardware you need"
  },
  consulting: {
    headline: "Empower Your Team with Expert IT Knowledge",
    subheadline: "Bridge the skills gap. Transform your workforce into confident, tech-savvy professionals.",
    overview: "Technology is only as powerful as the people using it. We provide strategic IT consulting and hands-on training programs that equip your team with the skills they need to leverage technology effectively, boost productivity, and drive innovation.",
    benefits: [
      { title: "Close the Skills Gap", description: "Bring your team up to speed on modern tools and technologies without hiring new staff." },
      { title: "Increase Productivity", description: "Trained employees work faster, make fewer mistakes, and require less IT support." },
      { title: "Reduce Technology Resistance", description: "Proper training builds confidence and turns skeptics into advocates for new systems." },
      { title: "Strategic Technology Decisions", description: "Get expert guidance on technology investments that align with your business goals." }
    ],
    process: [
      { step: "Skills Assessment", description: "We evaluate your team's current capabilities and identify knowledge gaps that need addressing." },
      { step: "Custom Program Design", description: "We design training programs tailored to your industry, tools, and specific business needs." },
      { step: "Interactive Training", description: "Hands-on workshops and practical exercises ensure knowledge retention and real-world application." },
      { step: "Follow-up Support", description: "Post-training resources, Q&A sessions, and refresher courses ensure lasting impact." }
    ],
    cta: "Upskill your team today"
  },
}

const ServiceDetailPage = () => {
  const { serviceId } = useParams()
  const service = servicesData.find(s => s.id === serviceId)
  const content = serviceContent[serviceId]

  // Redirect if service not found
  if (!service || !content) {
    return <Navigate to="/services" replace />
  }

  // SEO meta descriptions for each service
  const seoDescriptions = {
    brand: `Professional brand identity design in Nigeria. Logo design, brand guidelines, visual identity systems. Build a brand that commands trust and increases revenue by 23%.`,
    software: `Custom software development in Nigeria. Web apps, mobile apps, fintech solutions, APIs, PWAs. Build software that automates operations and multiplies revenue.`,
    ai: `AI and automation solutions in Nigeria. Chatbots, document processing, workflow automation, predictive analytics. Let AI handle the grunt work 24/7.`,
    transform: `Digital transformation services in Nigeria. Business process digitization, legacy modernization, ERP/CRM implementation, cloud migration.`,
    ux: `UI/UX design services in Nigeria. User research, wireframing, prototyping, design systems. Create experiences that convert visitors to customers.`,
    data: `Data analytics and business intelligence in Nigeria. Custom dashboards, Power BI, Tableau, predictive analytics. Turn data into actionable insights.`,
    marketing: `Digital marketing services in Nigeria. SEO, social media, PPC, content marketing, WhatsApp Business API. Be visible where your customers are.`,
    security: `Cybersecurity services in Nigeria. Security audits, NDPR compliance, incident response, employee training. Protect your digital assets.`,
    hardware: `Business hardware and IT equipment in Nigeria. Laptops, POS systems, barcode scanners, network equipment. Delivered configured and ready.`,
    consulting: `IT consulting and training in Nigeria. Technology strategy, software training, developer upskilling, digital literacy workshops.`,
  }

  return (
    <div className="service-detail-page">
      <Helmet>
        <title>{service.title} Services in Nigeria | FIGO Digital Services</title>
        <meta name="description" content={seoDescriptions[serviceId]} />
        <meta name="keywords" content={`${service.title}, ${service.title} Nigeria, ${service.title} Abuja, ${service.title} Lagos, digital services Nigeria`} />
        <link rel="canonical" href={`https://figodigitalservices.com/services/${serviceId}`} />
        <meta property="og:title" content={`${service.title} Services | FIGO Digital Services`} />
        <meta property="og:description" content={seoDescriptions[serviceId]} />
        <meta property="og:url" content={`https://figodigitalservices.com/services/${serviceId}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={`${service.title} Services | FIGO Digital Services`} />
        <meta name="twitter:description" content={seoDescriptions[serviceId]} />
      </Helmet>

      {/* Hero Section */}
      <section className="service-hero" style={{ '--service-color': service.color }}>
        <div
          className="service-hero-bg"
          style={{ backgroundImage: `url(${serviceHeroImages[serviceId]})` }}
        />
        <div className="service-hero-overlay" />
        <div className="service-hero-container">
          <motion.div
            className="service-hero-content"
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="service-hero-icon">{getServiceIcon(service.id, '#fff')}</div>
            <h1 className="service-hero-title">{content.headline}</h1>
            <p className="service-hero-subtitle">{content.subheadline}</p>
            <Link to="/contact" className="service-hero-cta">
              Get Started
              <span>→</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Service Name Badge */}
      <div className="service-name-badge-wrapper">
        <span className="service-name-badge" style={{ '--service-color': service.color }}>
          {getServiceIcon(service.id, service.color)}
          {service.title}
        </span>
      </div>

      {/* Overview Section */}
      <section className="service-overview">
        <div className="service-section-container">
          <div className="overview-content">
            <span className="section-eyebrow">Overview</span>
            <h2 className="section-title">What We Offer</h2>
            <p className="overview-text">{content.overview}</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="service-benefits">
        <div className="service-section-container">
          <span className="section-eyebrow">Benefits</span>
          <h2 className="section-title">
            How This Empowers Your Business
          </h2>
          <div className="benefits-grid">
            {content.benefits.map((benefit, index) => (
              <div
                key={index}
                className="benefit-card"
                style={{ '--service-color': service.color }}
              >
                <div className="benefit-icon-number" style={{ background: service.color }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="service-included">
        <div className="service-section-container">
          <span className="section-eyebrow">Deliverables</span>
          <h2 className="section-title">
            What's Included
          </h2>
          <div className="included-list">
            {service.details.map((detail, index) => {
              const isBonus = detail.startsWith('🎁');
              const cleanDetail = detail.replace(/^[✓🎁]\s*/, '');
              return (
                <div key={index} className={`included-item ${isBonus ? 'bonus' : ''}`}>
                  <span className="included-icon" style={{ background: isBonus ? '#22c55e' : service.color }}>
                    {isBonus ? (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1L10 5.5L15 6.5L11.5 10L12.5 15L8 12.5L3.5 15L4.5 10L1 6.5L6 5.5L8 1Z" fill="currentColor"/></svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M13.707 4.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L7 9.586l5.293-5.293a1 1 0 011.414 0z" fill="currentColor"/></svg>
                    )}
                  </span>
                  <span className="included-text">{cleanDetail}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="service-process">
        <div className="service-section-container">
          <span className="section-eyebrow">How We Work</span>
          <h2 className="section-title">
            Our Process
          </h2>
          <div className="process-steps">
            {content.process.map((step, index) => (
              <div
                key={index}
                className="process-step"
                style={{ '--service-color': service.color }}
              >
                <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.step}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Impact Section */}
      <section className="business-impact-section">
        <div className="service-section-container">
          <span className="section-eyebrow">Results</span>
          <h2 className="section-title">
            Business Impact
          </h2>
          <p
            className="section-subtitle"
            style={{
              textAlign: 'center',
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto 3rem',
              fontSize: '1.1rem'
            }}
          >
            What digital transformation can achieve for your business
          </p>
          <div className="impact-grid">
            {[
              {
                value: '40–60%',
                label: 'Productivity Potential',
                description: 'Streamlined workflows and automated processes',
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                  </svg>
                )
              },
              {
                value: '3x',
                label: 'Faster Reporting',
                description: 'Real-time dashboards and automated reports',
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                )
              },
              {
                value: '80%+',
                label: 'Error Reduction Goal',
                description: 'Fewer errors, better compliance, audit trails',
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                )
              },
              {
                value: '100%',
                label: 'Leadership Visibility',
                description: 'Real-time insights for data-driven decisions',
                icon: (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                )
              }
            ].map((impact, index) => (
              <div
                key={index}
                className="impact-card"
                style={{ '--service-color': service.color }}
              >
                <div className="impact-icon" style={{ color: service.color }}>
                  {impact.icon}
                </div>
                <div className="impact-value">{impact.value}</div>
                <h3 className="impact-label">{impact.label}</h3>
                <p className="impact-description">{impact.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta-section" style={{ '--service-color': service.color }}>
        <div
          className="service-cta-bg"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&auto=format&fit=crop&q=80)` }}
        />
        <div className="service-cta-overlay" />
        <div className="service-section-container">
          <div className="cta-content">
            <h2 className="cta-title">{content.cta}</h2>
            <p className="cta-text">
              Let's discuss how {service.title.toLowerCase()} can help your business grow.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="cta-primary">
                Book a Free Consultation
              </Link>
              <Link to="/services" className="cta-secondary">
                Explore Other Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services Section */}
      <section className="other-services">
        <div className="service-section-container">
          <h2 className="section-title">
            Explore Our Other Services
          </h2>
          <div className="other-services-grid">
            {servicesData
              .filter(s => s.id !== service.id)
              .map((otherService) => (
                otherService.externalLink ? (
                  <a
                    key={otherService.id}
                    href={otherService.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="other-service-card"
                    style={{ '--card-color': otherService.color }}
                  >
                    <span className="other-service-icon">{getServiceIcon(otherService.id, '#fff')}</span>
                    <div className="other-service-info">
                      <h3 className="other-service-title">{otherService.title}</h3>
                      <p className="other-service-desc">{otherService.description}</p>
                    </div>
                    <span className="other-service-arrow">→</span>
                  </a>
                ) : (
                  <Link
                    key={otherService.id}
                    to={`/services/${otherService.linkTo || otherService.id}`}
                    className="other-service-card"
                    style={{ '--card-color': otherService.color }}
                  >
                    <span className="other-service-icon">{getServiceIcon(otherService.id, '#fff')}</span>
                    <div className="other-service-info">
                      <h3 className="other-service-title">{otherService.title}</h3>
                      <p className="other-service-desc">{otherService.description}</p>
                    </div>
                    <span className="other-service-arrow">→</span>
                  </Link>
                )
              ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default ServiceDetailPage
